import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Axios from 'axios';
import { AuthContext } from 'components/lib';
import { useInbox } from 'components/hooks/useInbox';
import Style from './GlobalHeader.module.scss';

// ── Helpers ────────────────────────────────────────────────────
// Avatar neutro: el amarillo queda reservado para acciones.
const AVATAR_BG = '#1E293B';

function useOutsideClick(ref, handler) {
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) handler(); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [ref, handler]);
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min  = Math.floor(diff / 60_000);
  if (min < 1)  return 'Ahora';
  if (min < 60) return `Hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `Hace ${h}h`;
  return `Hace ${Math.floor(h / 24)}d`;
}

// ── Global search ──────────────────────────────────────────────
const SEARCH_CATEGORIES = [
  { key: 'routes',   label: 'Rutas',       link: r => `/route-editor?routeId=${r.id}`,  display: r => r.name,   sub: r => r.vehicle?.driver ?? '—' },
  { key: 'drivers',  label: 'Conductores', link: () => '/drivers',                       display: d => d.name,   sub: d => d.license ?? '' },
  { key: 'vehicles', label: 'Vehículos',   link: () => '/vehicles',                      display: v => `${v.brand ?? ''} ${v.model ?? ''}`.trim(), sub: v => v.plate ?? '' },
  { key: 'clients',  label: 'Clientes',    link: () => '/clients',                       display: c => c.name,   sub: c => c.email ?? '' },
];

const APIS = {
  routes:   '/api/routes',
  drivers:  '/api/driver',
  vehicles: '/api/vehicle',
  clients:  '/api/client',
};

function GlobalSearch() {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState(null);
  const [allData, setAllData] = useState(null);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const ref      = useRef(null);
  const navigate = useNavigate();
  useOutsideClick(ref, () => { setOpen(false); setQuery(''); setResults(null); });

  // Fetch all data once on first focus
  const fetchAll = useCallback(async () => {
    if (allData) return;
    setLoading(true);
    const results = await Promise.allSettled(
      Object.entries(APIS).map(([k, url]) =>
        Axios.get(url).then(r => [k, r.data?.data || []])
      )
    );
    const data = {};
    results.forEach(r => { if (r.status === 'fulfilled') { const [k, v] = r.value; data[k] = v; } });
    setAllData(data);
    setLoading(false);
  }, [allData]);

  // Filter on query change
  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults(null); setOpen(false); return; }
    if (!allData) return;
    const q = query.toLowerCase();
    const filtered = {};
    SEARCH_CATEGORIES.forEach(({ key, display, sub }) => {
      filtered[key] = (allData[key] || []).filter(item => {
        const d = display(item)?.toLowerCase() ?? '';
        const s = sub(item)?.toLowerCase() ?? '';
        return d.includes(q) || s.includes(q);
      }).slice(0, 4);
    });
    const hasAny = Object.values(filtered).some(a => a.length > 0);
    setResults(hasAny ? filtered : {});
    setOpen(true);
  }, [query, allData]);

  function handleSelect(cat, item) {
    navigate(cat.link(item));
    setOpen(false); setQuery(''); setResults(null);
  }

  const totalResults = results ? Object.values(results).reduce((s, a) => s + a.length, 0) : 0;

  return (
    <div ref={ref} className={Style.searchWrap}>
      <div className={`${Style.searchBar} ${open ? Style.searchBarOpen : ''}`}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          className={Style.searchInput}
          placeholder="Buscar rutas, conductores, vehículos..."
          value={query}
          onFocus={fetchAll}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Escape' && (setOpen(false), setQuery(''), setResults(null))}
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults(null); setOpen(false); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 2px', lineHeight: 1 }}
          >×</button>
        )}
      </div>

      {open && (
        <div className={Style.searchDropdown}>
          {loading && <p className={Style.searchDropEmpty}>Cargando datos…</p>}
          {!loading && results && totalResults === 0 && (
            <p className={Style.searchDropEmpty}>Sin resultados para "{query}"</p>
          )}
          {!loading && results && totalResults > 0 && SEARCH_CATEGORIES.map(cat => {
            const items = results[cat.key] || [];
            if (!items.length) return null;
            return (
              <div key={cat.key} className={Style.searchGroup}>
                <span className={Style.searchGroupLabel}>{cat.label}</span>
                {items.map(item => (
                  <button
                    key={item.id}
                    className={Style.searchItem}
                    onClick={() => handleSelect(cat, item)}
                  >
                    <span className={Style.searchItemName}>{cat.display(item)}</span>
                    {cat.sub(item) && (
                      <span className={Style.searchItemSub}>{cat.sub(item)}</span>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── School selector ────────────────────────────────────────────
function SchoolSelector() {
  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(null);
  const [schools, setSchools]   = useState([]);
  const ref = useRef(null);
  useOutsideClick(ref, () => setOpen(false));

  useEffect(() => {
    Axios.get('/api/school')
      .then(res => setSchools(res.data?.data || []))
      .catch(() => {});
  }, []);

  const label = selected
    ? (schools.find(s => s.id === selected)?.name ?? 'Colegio')
    : 'Colegio';

  return (
    <div ref={ref} className={Style.selectorWrap}>
      <button className={Style.selectorBtn} onClick={() => setOpen(v => !v)}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        <span className={Style.selectorLabel}>{label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className={Style.selectorDropdown}>
          <button className={`${Style.selectorItem} ${!selected ? Style.selectorItemActive : ''}`}
            onClick={() => { setSelected(null); setOpen(false); }}>
            Todos los colegios
          </button>
          {schools.map(s => (
            <button key={s.id}
              className={`${Style.selectorItem} ${selected === s.id ? Style.selectorItemActive : ''}`}
              onClick={() => { setSelected(s.id); setOpen(false); }}>
              {s.name}
            </button>
          ))}
          {!schools.length && <div className={Style.selectorEmpty}>Sin colegios</div>}
        </div>
      )}
    </div>
  );
}

// ── Period selector ────────────────────────────────────────────
function PeriodSelector() {
  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(null);
  const [periods, setPeriods]   = useState([]);
  const ref = useRef(null);
  useOutsideClick(ref, () => setOpen(false));

  useEffect(() => {
    Axios.get('/api/period')
      .then(res => {
        const data = res.data?.data || [];
        setPeriods(data);
        if (!selected && data.length > 0) {
          const cur = data.find(p => p.status === 'current' || p.status === 'active');
          if (cur) setSelected(cur.id);
        }
      })
      .catch(() => {});
  }, []);

  const label = selected
    ? (periods.find(p => p.id === selected)?.name ?? 'Periodo')
    : 'Periodo';

  return (
    <div ref={ref} className={Style.selectorWrap}>
      <button className={Style.selectorBtn} onClick={() => setOpen(v => !v)}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span className={Style.selectorLabel}>{label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className={Style.selectorDropdown}>
          {!periods.length
            ? <div className={Style.selectorEmpty}>Sin periodos</div>
            : periods.map(p => (
              <button key={p.id}
                className={`${Style.selectorItem} ${selected === p.id ? Style.selectorItemActive : ''}`}
                onClick={() => { setSelected(p.id); setOpen(false); }}>
                {p.name}
              </button>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ── helpers pago ───────────────────────────────────────────────
const MONTHS_ES_BELL = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
function fmtPayLabel(c) {
  const d = c.days_until_due;
  const dias = Math.abs(d);
  if (d < 0)   return { text: `Factura atrasada ${dias} día${dias === 1 ? '' : 's'}.`, level: 'danger' };
  if (d === 0) return { text: 'La factura vence hoy.',                                  level: 'warning' };
  return { text: `Factura por pagar en ${dias} día${dias === 1 ? '' : 's'}.`,           level: 'warning' };
}

/***
*   Cada alerta se distingue por dos cosas:
*   - la CATEGORÍA define el dibujo (ruta, vehículo, cobro, asistencia, sistema)
*   - el NIVEL define el color (rojo, ámbar, azul, gris)
*   Así dos alertas del mismo color siguen siendo distinguibles de un vistazo.
**********/
export const AlertIcon = {
  route: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="19" r="2.5"/><circle cx="18" cy="5" r="2.5"/>
      <path d="M8.5 19h7a3.5 3.5 0 0 0 0-7h-7a3.5 3.5 0 0 1 0-7h6"/>
    </svg>
  ),
  vehicle: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17V7h11v10"/><path d="M14 10h4l3 3.5V17h-7"/>
      <circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/>
    </svg>
  ),
  payment: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5"/><line x1="2.5" y1="10" x2="21.5" y2="10"/>
      <line x1="6" y1="14.5" x2="9.5" y2="14.5"/>
    </svg>
  ),
  attendance: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3.5h6v3H9z"/><path d="M7 5H5.5A1.5 1.5 0 0 0 4 6.5v13A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 18.5 5H17"/>
      <polyline points="8.5 13 11 15.5 15.5 11"/>
    </svg>
  ),
  system: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4 5.3 5.3"/>
    </svg>
  ),
  alert: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 2 20h20L12 3z"/><line x1="12" y1="10" x2="12" y2="14"/><line x1="12" y1="17.3" x2="12" y2="17.3"/>
    </svg>
  ),
};

// Cuando el texto no dice nada, el nivel sale de la categoría: así no
// termina todo en azul.
const LEVEL_BY_CATEGORY = {
  route:      'warning',
  vehicle:    'warning',
  payment:    'warning',
  attendance: 'info',
  system:     'neutral',
  alert:      'info',
};

// El backend aún no manda tipo ni nivel; se infieren del texto.
export function alertLevel(msg) {
  if (msg?.level) return msg.level;

  const text = `${msg?.title ?? ''} ${msg?.body ?? ''}`.toLowerCase();
  if (/(crític|critic|desvío|desvio|urgente|falla|error|pánico|panico|atrasad|accidente|no aborda|fuera de)/.test(text)) return 'danger';
  if (/(retras|exceso|velocidad|pendiente|vence|advertenc|mantenim|por pagar|próxim|proxim)/.test(text)) return 'warning';
  if (/(completad|finaliz|exitos|correct|entregad|al día|al dia)/.test(text)) return 'neutral';

  if (msg?.isRead) return 'neutral';
  return LEVEL_BY_CATEGORY[alertCategory(msg)] || 'info';
}

export function alertCategory(msg) {
  if (msg?.category) return msg.category;
  const text = `${msg?.title ?? ''} ${msg?.body ?? ''}`.toLowerCase();
  if (/(ruta|recorrido|desvío|desvio|parada|corredor)/.test(text))            return 'route';
  if (/(vehícul|vehicul|unidad|bus|placa|mantenim|combustible)/.test(text))   return 'vehicle';
  if (/(factur|pago|cobro|vence|atrasad|comprobante|deuda)/.test(text))       return 'payment';
  if (/(asistenc|abord|pasajer|estudiante|registr)/.test(text))               return 'attendance';
  if (/(sistema|actualiz|parámetro|parametro|configur)/.test(text))           return 'system';
  return 'alert';
}

function AlertRow({ level, category = 'alert', unread, title, time, text, actionLabel, onAction, onClick }) {
  const iconClass = {
    danger:  Style.bellIconDanger,
    warning: Style.bellIconWarning,
    info:    Style.bellIconInfo,
    neutral: Style.bellIconNeutral,
  }[level] || Style.bellIconNeutral;

  const unreadClass = !unread ? '' :
    level === 'danger'  ? Style.bellItemUnread :
    level === 'warning' ? Style.bellItemUnreadWarning :
                          Style.bellItemUnreadNeutral;

  return (
    <div className={`${Style.bellItem} ${unreadClass}`} role="button" tabIndex={0}
      onClick={onClick} onKeyDown={e => e.key === 'Enter' && onClick?.()}>

      <span className={`${Style.bellItemIcon} ${iconClass}`}>
        {AlertIcon[category] || AlertIcon.alert}
      </span>

      <div className={Style.bellItemBody}>
        <div className={Style.bellItemTop}>
          <p className={Style.bellItemTitle}>{title}</p>
          {time && <span className={Style.bellItemTime}>{time}</span>}
        </div>
        {text && <p className={Style.bellItemText}>{text}</p>}
        {actionLabel && (
          <button
            className={Style.bellItemAction}
            onClick={e => { e.stopPropagation(); onAction?.(); }}
          >
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Bell dropdown ──────────────────────────────────────────────
function BellDropdown({ messages, unreadCount, markOne, markAll, cobros }) {
  const [open,      setOpen]      = useState(false);
  const [payAlerts, setPayAlerts] = useState([]);
  const ref      = useRef(null);
  const navigate = useNavigate();
  useOutsideClick(ref, () => setOpen(false));

  useEffect(() => {
    // Las alertas de cobro son trabajo de contabilidad: el admin no las ve.
    if (!cobros) { setPayAlerts([]); return; }
    Axios.get('/api/clients').then(res => {
      const all = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const alerts = all.filter(c => {
        const d = c.days_until_due;
        if (d === null || d === undefined) return false;
        return d < 0 || (d <= 5 && Number(c.pending_no_receipt) > 0);
      }).sort((a, b) => (a.days_until_due ?? 999) - (b.days_until_due ?? 999)).slice(0, 10);
      setPayAlerts(alerts);
    }).catch(() => {});
  }, [cobros]);

  function dismissPay(item) {
    setPayAlerts(prev => prev.filter(c => c !== item));
  }

  const totalBadge = unreadCount + payAlerts.length;

  return (
    <div ref={ref} className={Style.iconWrap}>
      <button
        className={`${Style.iconBtn} ${totalBadge > 0 ? Style.iconBtnActive : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-label="Notificaciones"
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9a6 6 0 0 1 12 0c0 3.2.7 5 1.5 6H4.5C5.3 14 6 12.2 6 9z"/>
          <path d="M10.5 18.5a1.5 1.5 0 0 0 3 0"/>
        </svg>
        {totalBadge > 0 && (
          <span className={Style.iconBadge}>{totalBadge > 99 ? '99+' : totalBadge}</span>
        )}
      </button>

      {open && (
        <div className={Style.bellDropdown}>
          <div className={Style.bellDropHeader}>
            <span className={Style.bellDropTitle}>Notificaciones</span>
            {unreadCount > 0 && (
              <button className={Style.bellMarkAll} onClick={markAll}>Marcar todo como leído</button>
            )}
          </div>

          <div className={Style.bellDropList}>

            {/* ── Mensajes ── */}
            {messages.map(msg => {
              const level = alertLevel(msg);
              return (
                <AlertRow
                  key={msg.id}
                  level={level}
                  category={alertCategory(msg)}
                  unread={!msg.isRead}
                  title={msg.title}
                  time={timeAgo(msg.createdAt)}
                  text={msg.body}
                  actionLabel={msg.actionUrl ? 'Ver detalle' : null}
                  onAction={() => { window.location.href = msg.actionUrl; }}
                  onClick={() => { if (!msg.isRead) markOne(msg.id); }}
                />
              );
            })}

            {/* ── Facturación ── */}
            {payAlerts.length > 0 && (
              <>
                <div className={Style.bellSection}>Facturación</div>
                {payAlerts.map((c, i) => {
                  const { text, level } = fmtPayLabel(c);
                  return (
                    <AlertRow
                      key={`pay-${i}`}
                      level={level}
                      category="payment"
                      unread
                      title={c.name || 'Cliente'}
                      time={c.days_until_due < 0 ? 'Vencida' : 'Próxima'}
                      text={text}
                      actionLabel="Ver factura"
                      onAction={() => { setOpen(false); navigate('/accounting/billing'); }}
                      onClick={() => dismissPay(c)}
                    />
                  );
                })}
              </>
            )}

            {/* ── Vacío ── */}
            {!payAlerts.length && !messages.length && (
              <p className={Style.bellDropEmpty}>Sin alertas pendientes</p>
            )}

          </div>

          <div className={Style.bellDropFooter}>
            <button
              className={Style.bellDropFooterLink}
              onClick={() => { setOpen(false); navigate('/notifications'); }}
            >
              Ver historial completo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Profile — sutil, sin borde visible ────────────────────────
function ProfileCard({ user, roleLabel, signout }) {
  const [open, setOpen] = useState(false);
  const ref      = useRef(null);
  const navigate = useNavigate();
  useOutsideClick(ref, () => setOpen(false));

  const initial = user?.name?.[0]?.toUpperCase() ?? 'U';

  return (
    <div ref={ref} className={Style.profileWrap}>
      <button className={Style.profileBtn} onClick={() => setOpen(v => !v)}>
        <div className={Style.profileAvatar} style={{ background: AVATAR_BG }}>{initial}</div>
        <div className={Style.profileInfo}>
          <span className={Style.profileName}>{user?.name?.split(' ')[0] ?? 'Usuario'}</span>
          {roleLabel && <span className={Style.profileRole}>{roleLabel}</span>}
        </div>
      </button>

      {open && (
        <div className={Style.profileDropdown}>
          <div className={Style.profileDropHeader}>
            <div className={Style.profileDropAvatar} style={{ background: AVATAR_BG }}>{initial}</div>
            <div>
              <span className={Style.profileDropName}>{user?.name ?? 'Usuario'}</span>
              {roleLabel && <span className={Style.profileDropRole}>{roleLabel}</span>}
            </div>
          </div>
          <div className={Style.profileDropBody}>
            <button className={Style.profileDropItem}
              onClick={() => { navigate('/account'); setOpen(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              Mi cuenta
            </button>
            <button className={Style.profileDropItem}
              onClick={() => { navigate('/notifications'); setOpen(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M12 3a7 7 0 0 0-7 7v4l-1.5 2.5h17L19 14v-4a7 7 0 0 0-7-7z"/>
                <path d="M10 20a2 2 0 0 0 4 0"/>
              </svg>
              Notificaciones
            </button>
            <div className={Style.profileDropDivider}/>
            <button className={`${Style.profileDropItem} ${Style.profileDropItemDanger}`}
              onClick={signout}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main GlobalHeader ──────────────────────────────────────────
export function GlobalHeader() {
  const navigate    = useNavigate();
  const authContext = useContext(AuthContext);
  const user        = authContext.user;
  const { messages, unreadCount, markOne, markAll } = useInbox();

  const roleLabel = {
    master:      'Propietario',
    owner:       'Dueño',
    admin:       'Administrador',
    coordinator: 'Coordinador',
    accounting:  'Contador',
    user:        'Usuario',
  }[user?.permission] ?? '';

  // Solo quien lleva la contabilidad ve las alertas de cobro en la campana.
  const verCobros = user?.permission === 'accounting' || user?.permission === 'master';

  return (
    <header className={Style.header}>

      {/* ── LEFT: frase fija; el título del módulo vive en la página ─── */}
      <div className={Style.left}>
        <p className={Style.tagline}>Gestión de transporte escolar</p>
      </div>

      {/* ── CENTER: búsqueda global ─────────── */}
      <div className={Style.center}>
        <GlobalSearch />
      </div>

      {/* ── RIGHT: chat + bell + perfil sutil ──── */}
      <div className={Style.right}>

        {/* Bell */}
        <BellDropdown
          messages={messages}
          unreadCount={unreadCount}
          markOne={markOne}
          markAll={markAll}
          cobros={verCobros}
        />

        {/* Ayuda */}
        <button
          className={Style.iconBtn}
          onClick={() => navigate('/help')}
          aria-label="Ayuda"
          title="Ayuda"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M9.6 9.2a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.4"/>
            <line x1="12" y1="17" x2="12" y2="17"/>
          </svg>
        </button>

        {/* Profile sutil */}
        <ProfileCard
          user={user}
          roleLabel={roleLabel}
          signout={() => authContext.signout()}
        />

      </div>
    </header>
  );
}
